import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useIntl } from '@leagueplatform/locales';
import { useQueryClient } from '@tanstack/react-query';
import {
  useResolvedEntity,
  useConfigAppParams,
  getEffectiveEntityId,
  useConfigAppPath,
  useSearchParam,
  useConfigUserFeedback,
  useEntityOperationError,
  useAuthoringEnvironments,
  useConfigApp,
  EntityDetailsDataStoreProvider,
  RuleTemplatesEditorStateStoreProvider,
  AiAssistPromptsEditorStateStoreProvider,
  useSetEntityData,
  useAddConfigMessage,
  createBulkActionReportingStore,
  buildOperationErrorDetail,
} from '@web-config-app/core-react';
import type {
  GetEntityDetailsPathForEnvAndId,
  OtherAuthoringEnvironmentCandidate,
} from '@web-config-app/core-react';
import { DataSourceStoreProvider } from '@web-config-app/entity-form';
import { EntityDetailsPageContainer } from '@web-config-app/core-react-containers';
import type {
  ConfigMessageInfo,
  DomainEntity,
  EnvironmentKey,
} from '@web-config-app/core';
import { entityDetailActionPendingKey } from '@web-config-app/core';
import { ErrorPage } from '@web-config-app/core-react-ui';
import { useCustomActionEventHandlers } from '../../hooks/use-custom-action-event-handlers/use-custom-action-event-handlers';
import { useCommonOperationEventHandlers } from '../../hooks/use-common-operation-event-handlers/use-common-operation-event-handlers';
import { ConfigAppEntityDetailsPageRendererWithEntityDetailsContext } from './config-app-entity-details-page-renderer-with-entity-details-context.component';
import { handleEventMessageResponse } from '../../utils/handle-event-message-response';

/**
 * Component that provides custom action event handlers with access to setEntityData
 */
const ConfigAppEntityDetailsPageWithProviderAccess = ({
  domainEntity,
  entityInstanceId,
  entityListPath,
  environmentKey,
  getEntityDetailsPathForEnvAndId,
  getEntityDetailsPathForId,
  entityName,
  listQueryKey,
  queryClient,
  resetConfigUserFeedbackStore,
  setEntityOperationLoadingMsg,
  handleEntityOperationError,
  onSettled,
  navigate,
  otherAuthoringEnvironments,
  path,
  hasEntityId,
  currentAuthoringEnvironment,
  addConfigMessage,
  tenantId,
}: {
  domainEntity: DomainEntity;
  entityInstanceId?: string;
  entityListPath: string;
  environmentKey?: EnvironmentKey;
  getEntityDetailsPathForEnvAndId: GetEntityDetailsPathForEnvAndId;
  getEntityDetailsPathForId: (id: string) => string;
  entityName: string;
  listQueryKey: any[];
  queryClient: any;
  resetConfigUserFeedbackStore: () => void;
  setEntityOperationLoadingMsg: (msg: string) => void;
  handleEntityOperationError: (error: any) => void;
  onSettled: () => void;
  navigate: any;
  otherAuthoringEnvironments: OtherAuthoringEnvironmentCandidate[];
  path: string;
  hasEntityId: boolean;
  currentAuthoringEnvironment: EnvironmentKey;
  addConfigMessage: (message: ConfigMessageInfo) => void;
  tenantId: string;
}) => {
  const { formatMessage } = useIntl();
  const { getEnvironmentLabel } = useAuthoringEnvironments();
  const setEntityData = useSetEntityData();
  const createCustomActionEventHandlers = useCustomActionEventHandlers();
  const customActionEventHandlers =
    createCustomActionEventHandlers(setEntityData);

  const bulkActionReportingStore = React.useMemo(
    () => createBulkActionReportingStore(),
    [],
  );

  return (
    <EntityDetailsPageContainer
      tenantId={tenantId}
      domainEntity={domainEntity}
      bulkActionReportingStore={bulkActionReportingStore}
      instanceId={entityInstanceId!}
      currentAuthoringEnvironment={currentAuthoringEnvironment}
      path={path}
      createDefaultEntityData={!hasEntityId}
      otherAuthoringEnvironments={otherAuthoringEnvironments}
      options={{
        get: {
          retry: 1,
        },
        update: {
          onMutate: (operation) => {
            const loadingMessage = formatMessage(
              { id: entityDetailActionPendingKey[operation] },
              { entity: entityName },
            );
            setEntityOperationLoadingMsg(loadingMessage);
          },
          onSuccess: (response) => {
            queryClient.invalidateQueries({
              queryKey: listQueryKey,
            });
            queryClient.invalidateQueries({
              queryKey: [
                environmentKey,
                domainEntity.endpoints.get.path,
                entityInstanceId,
              ],
            });
            resetConfigUserFeedbackStore();
            handleEventMessageResponse({
              eventMessage: response?.meta?.eventMessage,
              addConfigMessage,
              formatMessage,
            });
          },
          onError: (error: any) => {
            handleEntityOperationError(error);
          },
          onSettled,
        },
        create: {
          onMutate: (operation) => {
            const loadingMessage = formatMessage(
              { id: entityDetailActionPendingKey[operation] },
              { entity: entityName },
            );
            setEntityOperationLoadingMsg(loadingMessage);
          },
          onSuccess: (response) => {
            queryClient.invalidateQueries({
              queryKey: listQueryKey,
            });
            const newEntityId = response?.data?.id;
            navigate(getEntityDetailsPathForId(newEntityId));
            resetConfigUserFeedbackStore();
            handleEventMessageResponse({
              eventMessage: response?.meta?.eventMessage,
              addConfigMessage,
              formatMessage,
            });
          },
          onError: (error: any) => {
            handleEntityOperationError(error);
          },
          onSettled,
        },
        clone: {
          onMutate: () => {
            bulkActionReportingStore.getState().createSession({
              operationType: 'clone',
              domainEntityType: domainEntity.type,
              sourceEnvironment: environmentKey,
              totalItems: 1,
            });
          },
          onSuccess: (response) => {
            queryClient.invalidateQueries({
              queryKey: listQueryKey,
            });

            const newEntityId = response?.data?.id;

            resetConfigUserFeedbackStore();

            const entityMetadata = response?.data?.attributes?.entityMetadata;
            bulkActionReportingStore.getState().recordItemResult({
              sourceEntityId: entityInstanceId ?? '',
              entityId: newEntityId,
              entityType: entityMetadata?.domainEntityType ?? '',
              entityName: entityMetadata?.name ?? entityName,
              domainEntityId: entityMetadata?.domainEntityId,
              status: 'success',
            });
            bulkActionReportingStore.getState().completeSession();

            handleEventMessageResponse({
              eventMessage: response?.meta?.eventMessage,
              addConfigMessage,
              formatMessage,
            });
          },
          onError: (error: any) => {
            bulkActionReportingStore.getState().recordItemResult({
              sourceEntityId: entityInstanceId ?? '',
              entityType: domainEntity.type,
              entityName,
              domainEntityId: undefined,
              status: 'error',
              error: buildOperationErrorDetail(error, formatMessage),
            });
            bulkActionReportingStore.getState().completeSession();
          },
          onSettled,
        },
        deepClone: {
          request: {
            onMutate: () => {
              bulkActionReportingStore.getState().createSession({
                operationType: 'clone',
                domainEntityType: domainEntity.type,
                sourceEnvironment: environmentKey,
                totalItems: 1,
              });
            },
            onSuccess: (response) => {
              queryClient.invalidateQueries({
                queryKey: listQueryKey,
              });

              resetConfigUserFeedbackStore();

              const store = bulkActionReportingStore.getState();
              const rootEntityMetadata =
                response?.data?.attributes?.entityMetadata;
              // Record the root entity
              store.recordItemResult({
                sourceEntityId: entityInstanceId ?? '',
                entityId: response?.data?.id,
                entityType: rootEntityMetadata?.domainEntityType ?? '',
                entityName: rootEntityMetadata?.name ?? entityName,
                domainEntityId: rootEntityMetadata?.domainEntityId,
                status: 'success',
              });
              // Record all included (child) entities
              response?.included?.forEach(
                (includedEntity: {
                  id: string;
                  attributes?: {
                    entityMetadata?: {
                      domainEntityType?: string;
                      domainEntityId?: string;
                      name?: string;
                    };
                  };
                }) => {
                  const metadata = includedEntity.attributes?.entityMetadata;
                  store.recordItemResult({
                    sourceEntityId: includedEntity.id,
                    entityId: includedEntity.id,
                    entityType: metadata?.domainEntityType ?? '',
                    entityName: metadata?.name,
                    domainEntityId: metadata?.domainEntityId,
                    status: 'success',
                  });
                },
              );
              store.updateTotalItems(1 + (response?.included?.length ?? 0));
              store.completeSession();

              handleEventMessageResponse({
                eventMessage: response?.meta?.eventMessage,
                addConfigMessage,
                formatMessage,
              });
            },
            onError: (error: any) => {
              bulkActionReportingStore.getState().recordItemResult({
                sourceEntityId: entityInstanceId ?? '',
                entityType: domainEntity.type,
                entityName,
                domainEntityId: undefined,
                status: 'error',
                error: buildOperationErrorDetail(error, formatMessage),
              });
              bulkActionReportingStore.getState().completeSession();
            },
            onSettled,
          },
        },
        validate: {
          onMutate: () => {
            const loadingMessage = formatMessage(
              { id: entityDetailActionPendingKey.validate },
              { entity: entityName },
            );
            setEntityOperationLoadingMsg(loadingMessage);
          },
          onSuccess: (response) => {
            resetConfigUserFeedbackStore();
            handleEventMessageResponse({
              eventMessage: response?.meta?.eventMessage ?? {
                status: 'success',
              },
              addConfigMessage,
              successMessage: formatMessage({ id: 'VALIDATION_PASSED' }),
              formatMessage,
            });
          },
          onError: (error: any) => {
            resetConfigUserFeedbackStore();
            handleEntityOperationError(error);
          },
          onSettled,
        },
        custom: {
          ...customActionEventHandlers,
          onSettled,
        },
      }}
      multiEnvironmentOptions={{
        syncToEnvironment: {
          onMutate: (operation, environment) => {
            const loadingMessage = formatMessage(
              { id: entityDetailActionPendingKey[operation] },
              { entity: entityName, environment },
            );
            setEntityOperationLoadingMsg(loadingMessage);
          },
          onSuccess: (response, environment) => {
            queryClient.invalidateQueries({
              queryKey: listQueryKey,
            });
            // Invalidate the other environment's entity data since that was the target environment for the sync. This ensures that the data compare modal is updated with the latest data.
            queryClient.invalidateQueries({
              queryKey: [
                environment,
                domainEntity.endpoints.get.path,
                entityInstanceId,
              ],
            });
            resetConfigUserFeedbackStore();
            handleEventMessageResponse({
              eventMessage: response?.meta?.eventMessage ?? {
                status: 'success',
              },
              successMessage: formatMessage({
                id: 'ENVIRONMENT_SYNCED_SUCCESSFULLY',
              }),
              addConfigMessage,
              formatMessage,
            });
          },
          onError: (error: any) => {
            handleEntityOperationError(error);
          },
          onSettled,
        },
        copyToEnvironment: {
          onSuccess: (_, environment: EnvironmentKey) => {
            if (entityInstanceId) {
              queryClient.invalidateQueries({
                queryKey: [
                  environment,
                  domainEntity.endpoints.get.path,
                  entityInstanceId,
                ],
              });

              resetConfigUserFeedbackStore();

              // The reporting modal stays open and provides a
              // "Switch environments" link, so we don't auto-navigate.

              handleEventMessageResponse({
                eventMessage: {
                  status: 'success',
                },
                addConfigMessage,
                successMessage: formatMessage(
                  { id: 'COPY_TO_ENVIRONMENT_SUCCESS' },
                  {
                    environment: getEnvironmentLabel(environment),
                  },
                ),
                formatMessage,
              });
            }
          },
          onError: (error: any) => {
            handleEntityOperationError(error);
          },
          onSettled,
        },
      }}
    >
      <ConfigAppEntityDetailsPageRendererWithEntityDetailsContext
        domainEntity={domainEntity}
        instanceId={entityInstanceId}
        entityListPath={entityListPath}
        environmentKey={environmentKey!}
        getEntityDetailsPathForEnvAndId={getEntityDetailsPathForEnvAndId}
      />
    </EntityDetailsPageContainer>
  );
};

/**
 * After we've loaded the domain entity, start rendering the contents of the page
 * and running some effects. All the logic in this component is dependent on having
 * a defined `domainEntity`, which is is loaded asynchronously in the outer
 * `ConfigAppEntityDetailsPage` component.
 */

const ConfigAppEntityDetailsPageWithDomainEntity = ({
  domainEntity,
  entityInstanceId,
  entityListPath,
  environmentKey,
  getEntityDetailsPathForId,
  getEntityDetailsPathForEnvAndId = (id: string) => `/entity/${id}`,
}: {
  domainEntity: DomainEntity;
  entityInstanceId?: string;
  entityListPath: string;
  environmentKey?: EnvironmentKey;
  getEntityDetailsPathForEnvAndId: GetEntityDetailsPathForEnvAndId;
  getEntityDetailsPathForId: (id: string) => string;
}) => {
  const { formatMessage } = useIntl();

  const navigate = useNavigate();
  const { state: locationState } = useLocation();
  const { tenantId: resolvedTenantId } = useConfigApp();
  const { tenantId: routeTenantId } = useConfigAppParams();
  const tenantId = routeTenantId ?? resolvedTenantId;

  /**
   * if the entityListSearchParams are present in the location state, that means that the user had an active search or pagination going when they navigated to the entity details page. When they exit the entity details page, we want to return them to the same state they were in on the entity list page.
   */
  const entityListPathWithParams = locationState?.entityListSearchParams
    ? `${entityListPath}?${locationState.entityListSearchParams}`
    : entityListPath;

  const queryClient = useQueryClient();

  const {
    otherAuthoringEnvironments: otherAuthoringEnvironmentKeys,
    currentAuthoringEnvironment,
    getEnvironmentLabel,
  } = useAuthoringEnvironments({
    environmentKey,
  });

  const otherAuthoringEnvironments: OtherAuthoringEnvironmentCandidate[] =
    React.useMemo(
      () =>
        otherAuthoringEnvironmentKeys.map((key) => ({
          environmentKey: key,
          name: getEnvironmentLabel(key),
        })),
      [otherAuthoringEnvironmentKeys, getEnvironmentLabel],
    );

  const path = useSearchParam('path') ?? '';

  const hasEntityId = Boolean(entityInstanceId);

  const { resetConfigUserFeedbackStore, setEntityOperationLoadingMsg } =
    useConfigUserFeedback((state) => ({
      resetConfigUserFeedbackStore: state.resetConfigUserFeedbackStore,
      setEntityOperationLoadingMsg: state.setEntityOperationLoadingMsg,
    }));

  const addConfigMessage = useAddConfigMessage();

  const handleEntityOperationError = useEntityOperationError();

  const entityName =
    domainEntity && formatMessage({ id: domainEntity.labelKey }, { count: 1 });

  const { onSettled } = useCommonOperationEventHandlers();

  const listQueryKey = React.useMemo(
    () =>
      domainEntity?.isUsingConfigAppBackend === false
        ? [environmentKey, domainEntity.endpoints.list.path]
        : [
            environmentKey,
            domainEntity?.endpoints.list.path,
            // invalidate dml (data points) so that any changes to entities creating DMLs are reflected
            'dml',
            domainEntity?.type,
            null,
          ],
    [domainEntity, environmentKey],
  );

  /**
   *Reset on unmount of the page
   */
  React.useEffect(
    () => () => {
      resetConfigUserFeedbackStore();
    },
    [resetConfigUserFeedbackStore],
  );

  return (
    <EntityDetailsDataStoreProvider>
      <RuleTemplatesEditorStateStoreProvider>
        <AiAssistPromptsEditorStateStoreProvider>
          <DataSourceStoreProvider>
            <ConfigAppEntityDetailsPageWithProviderAccess
              tenantId={tenantId}
              domainEntity={domainEntity}
              entityInstanceId={entityInstanceId}
              entityListPath={entityListPathWithParams}
              environmentKey={environmentKey}
              getEntityDetailsPathForEnvAndId={getEntityDetailsPathForEnvAndId}
              getEntityDetailsPathForId={getEntityDetailsPathForId}
              entityName={entityName}
              listQueryKey={listQueryKey}
              queryClient={queryClient}
              resetConfigUserFeedbackStore={resetConfigUserFeedbackStore}
              setEntityOperationLoadingMsg={setEntityOperationLoadingMsg}
              handleEntityOperationError={handleEntityOperationError}
              onSettled={onSettled}
              navigate={navigate}
              otherAuthoringEnvironments={otherAuthoringEnvironments}
              path={path}
              hasEntityId={hasEntityId}
              currentAuthoringEnvironment={currentAuthoringEnvironment}
              addConfigMessage={addConfigMessage}
            />
          </DataSourceStoreProvider>
        </AiAssistPromptsEditorStateStoreProvider>
      </RuleTemplatesEditorStateStoreProvider>
    </EntityDetailsDataStoreProvider>
  );
};

/**
 * This is the outermost rendered component. It defers rendering the contents until
 * we've been able to load the domain entity.
 */
export const ConfigAppEntityDetailsPage = () => {
  const params = useConfigAppParams<{
    tabId: string;
    moduleId: string;
    childModuleId: string;
    entityInstanceId: string;
    environmentKey: EnvironmentKey;
  }>();
  const { tabId, moduleId, childModuleId, entityInstanceId, environmentKey } =
    params;

  const generateConfigPaths = useConfigAppPath();

  const { entity: domainEntity, isError: isEntityError } = useResolvedEntity({
    entityId: getEffectiveEntityId(params),
  });

  const {
    getEntityDetailsPathForId,
    entityListPath,
    getEntityDetailsPathForEnvAndId,
  } = generateConfigPaths({
    tabId,
    moduleId,
    childModuleId,
    environmentKey,
  });

  if (isEntityError) {
    return <ErrorPage />;
  }

  if (!domainEntity) {
    return null;
  }

  return (
    <ConfigAppEntityDetailsPageWithDomainEntity
      domainEntity={domainEntity}
      entityInstanceId={entityInstanceId}
      entityListPath={entityListPath}
      environmentKey={environmentKey}
      getEntityDetailsPathForId={getEntityDetailsPathForId}
      getEntityDetailsPathForEnvAndId={getEntityDetailsPathForEnvAndId}
    />
  );
};
